<!-- df-frontdesk: add_session_dialog.dart / update_or_cancel_session_dialog.dart structure -->
<ng-template #content role="document" let-modal>
  <div class="su-root" [class.su-root--native-pickers]="useNativeSessionPickers">
    <!-- Flatpickr append target: must stay inside ngb-modal-window so body siblings are not aria-hidden while hour input is focused -->
    <div class="su-fpm"></div>
    <div class="su-m" [class.su-m--e]="isEditing()">
    <!-- Full-dialog trainer load (Flutter: Center CircularProgressIndicator replaces column) -->
    <div class="su-tl" *ngIf="!isEditing() && trainersLoading">
      <div class="spinner-border text-secondary" role="status"><span class="sr-only">Loading…</span></div>
    </div>

    <ng-container *ngIf="isEditing() || !trainersLoading">
      <div class="su-hd">
        <div class="su-hdt">
          <h2 class="su-tt">{{ isEditing() ? 'Edit' : 'Add' }} session</h2>
          <p class="su-st" *ngIf="!isEditing()">Schedule a training session</p>
        </div>
        <button type="button" class="su-x" aria-label="Close" (click)="modal.dismiss()">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div class="su-eb" *ngIf="addSessionErrorBanner && !isEditing()">
        <i class="ri-error-warning-line"></i>
        <span class="su-et">{{ addSessionErrorBanner }}</span>
        <button type="button" class="su-ex" aria-label="Dismiss" (click)="dismissAddSessionError()">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div class="su-bs">
        <form [formGroup]="form" class="su-fo">
          <!-- Trainer (add only) — outlined, muted empty / surface when selected -->
          <div class="su-f" *ngIf="!isEditing()">
            <label class="su-lb" for="su-tse">Trainer</label>
            <div class="su-ts" [class.su-ts--f]="selectedTrainerId != null">
              <select id="su-tse" class="su-tse"
                [ngModel]="selectedTrainerId" [ngModelOptions]="{standalone: true}" disabled
                title="Only the logged-in trainer can book sessions">
                <option *ngIf="trainersList.length === 0" [ngValue]="null">Trainer unavailable</option>
                <option *ngFor="let tr of trainersList" [ngValue]="tr.Id">
                  {{ trainerOptionLabel(tr) }}
                </option>
              </select>
            </div>
          </div>

          <!-- Session type chips from Training/Sessions/OfferedTypes -->
          <div class="su-f" *ngIf="!isEditing()">
            <label class="su-lb">Session type</label>
            <div class="su-tyr">
              <ng-container *ngFor="let t of sessionTypes">
                <input type="radio" class="su-ci" [id]="'asr' + t.code" [value]="t.code" formControlName="sessiontype">
                <label class="su-tp" [for]="'asr' + t.code">{{ t.label }}</label>
              </ng-container>
            </div>
          </div>

          <!-- Edit: Flutter order — subtitle, members, then type -->
          <p class="su-cx" *ngIf="isEditing()">
            {{ isAssessmentSession() ? 'Fitness assessment with' : isGroupSession() ? 'Group session with' : 'Private session with' }}
            {{ getTrainerFirstNameForSubtitle() }}
          </p>

          <div class="su-f" *ngIf="!isEditing() && selectedTrainerId != null">
            <p class="su-cx">
              {{ isAssessmentSession() ? 'Fitness assessment with' : isGroupSession() ? 'Group session with' : 'Private session with' }}
              {{ getTrainerFirstNameForSubtitle() }}
            </p>
          </div>

          <!-- Members: fixed height 200px + FAB (add_session_dialog _memberCellSection) -->
          <div class="su-f su-f--0">
            <div class="su-mh">
              <div class="su-mi">
                <ng-container *ngIf="!isEditing() && !hasTrainerForRoster()">
                  <span class="su-mp">Select a trainer to add client(s)</span>
                </ng-container>
                <ng-container *ngIf="(isEditing() || hasTrainerForRoster()) && clients.length === 0">
                  <span class="su-mp" *ngIf="isEditing() && clientsLoading">Loading member…</span>
                  <span class="su-mp" *ngIf="!(isEditing() && clientsLoading)">No clients added</span>
                </ng-container>
                <div class="su-crs" *ngIf="clients.length > 0">
                  <div class="su-crd" *ngFor="let item of clients; trackBy: trackMemberCard">
                    <img [src]="item.avatar" alt="" class="su-cav" (error)="item.avatar = 'assets/images/users/user-dummy-img.jpg'" />
                    <div class="su-cif">
                      <span class="su-cnm">
                        {{ item.name }}
                        <span class="su-cid-inline" *ngIf="item.id">ID: {{ item.id }}</span>
                      </span>
                      <span class="su-cme" *ngFor="let line of item.balanceLines">{{ line }}</span>
                    </div>
                    <i class="ri-checkbox-circle-fill su-cok" aria-hidden="true"></i>
                    <button *ngIf="removeAllowed(item)" type="button" class="su-crm" (click)="removeClient(item)" aria-label="Remove">
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                </div>
              </div>
              <button *ngIf="showAddClientButton()" type="button" class="su-fab" (click)="openSelectMembersModal()" aria-label="Add client">
                <i class="ri-add-line"></i>
              </button>
            </div>
          </div>

          <!-- Edit: session type after member list (update_or_cancel_session_dialog) -->
          <div class="su-f" *ngIf="isEditing()">
            <label class="su-lb">Session type</label>
            <div class="su-tyr su-tyr--ro" (click)="onSessionTypeChipClickWhenEditing()">
              <ng-container *ngFor="let t of sessionTypes">
                <input type="radio" class="su-ci" [id]="'esr' + t.code" [value]="t.code" formControlName="sessiontype">
                <label class="su-tp" [for]="'esr' + t.code">{{ t.label }}</label>
              </ng-container>
            </div>
            <p class="su-th">Type cannot be changed — credits differ by product.</p>
          </div>

          <!-- Start date: ngb-datepicker (modal-safe). Start time: Flatpickr time-only (h:i K, 15 min). -->
          <div class="su-gr">
            <div class="su-f su-fh">
              <label for="sessiondate" class="su-lb">Start date</label>
              <ng-container *ngIf="!useNativeSessionPickers">
                <div class="su-dpr su-io" #sessionDatePickerAnchor>
                  <input
                    id="sessiondate"
                    type="text"
                    class="su-in su-id"
                    tabindex="0"
                    formControlName="sessiondate"
                    ngbDatepicker
                    #sessionDateDp="ngbDatepicker"
                    [minDate]="ngbDateMinBound()"
                    [maxDate]="ngbDateMaxBound()"
                    container="body"
                    placement="bottom-left"
                    autoClose="outside"
                    [outsideDays]="'visible'"
                    datepickerClass="su-ngdp"
                    placeholder="Select date"
                    readonly
                    (click)="toggleBodyDatepicker(sessionDateDp, sessionDatePickerAnchor)"
                    (dateSelect)="onSessionDatePickerChange(); sessionDateDp.close()"
                  />
                  <button type="button" class="su-dpb" (click)="toggleBodyDatepicker(sessionDateDp, sessionDatePickerAnchor); $event.stopPropagation()" aria-label="Open calendar">
                    <i class="ri-calendar-line"></i>
                  </button>
                </div>
              </ng-container>
              <div class="su-io" *ngIf="useNativeSessionPickers">
                <input
                  id="sessiondate"
                  type="date"
                  class="su-in su-ntd"
                  tabindex="0"
                  [min]="sessionDateIsoMin()"
                  [max]="sessionDateIsoMax()"
                  [ngModel]="sessionDateIsoForNativeInput()"
                  [ngModelOptions]="{standalone: true}"
                  (ngModelChange)="onNativeSessionDateIsoChange($event)"
                  (focus)="onNativeSessionFieldFocus($event)"
                />
              </div>
            </div>
            <div class="su-f su-fh">
              <label for="sessiontime" class="su-lb">Start time</label>
              <ng-container *ngIf="!useNativeSessionPickers">
                <div class="su-fpa su-io">
                  <input
                    #sessionTime
                    id="sessiontime"
                    tabindex="0"
                    class="su-in su-ii text-nowrap text-left"
                    type="text"
                    mwlFlatpickr
                    [appendTo]="sessionTimeFlatpickrAppendTo"
                    [convertModelValue]="true"
                    [disableMobile]="true"
                    [noCalendar]="true"
                    [enableTime]="true"
                    [dateFormat]="'h:i K'"
                    [minuteIncrement]="15"
                    formControlName="sessiontime"
                    placeholder="Select time"
                    step="900"
                    (flatpickrOpen)="pinFlatpickrTimeInModal($event)"
                    (flatpickrChange)="onSessionTimeFlatpickrChange($event)"
                    (flatpickrMonthChange)="pinFlatpickrTimeInModal($event)"
                    (flatpickrYearChange)="pinFlatpickrTimeInModal($event)"
                  />
                  <i class="ri-time-line su-iic"></i>
                </div>
              </ng-container>
              <div class="su-io" *ngIf="useNativeSessionPickers">
                <input
                  id="sessiontime"
                  type="time"
                  class="su-in su-ntd"
                  tabindex="0"
                  step="900"
                  [ngModel]="sessionTimeHHmmForNativeInput()"
                  [ngModelOptions]="{standalone: true}"
                  (ngModelChange)="onNativeSessionTimeHHmmChange($event)"
                  (focus)="onNativeSessionFieldFocus($event)"
                />
              </div>
            </div>
          </div>

          <!-- Recurring shift card (add_session_recurrence_section.dart) — add only -->
          <div class="su-rc" *ngIf="!isEditing() && isTimeSet()">
            <button type="button" class="su-rtg" (click)="toggleRecurringShift()">
              <i class="ri-repeat-2-line" [class.text-muted]="!isRecurringShift"></i>
              <span [class.su-tms]="!isRecurringShift">Recurring shift</span>
              <span class="su-rsp"></span>
              <span class="su-sk" [class.su-sk--on]="isRecurringShift" aria-hidden="true"><i class="su-sn"></i></span>
            </button>

            <div class="su-rir" *ngIf="isRecurringShift">
              <div class="su-ref">
                <div class="su-rcl su-rcl--n">
                  <span class="su-rcp">EVERY</span>
                  <input type="text" inputmode="numeric" pattern="[0-9]*" class="su-rin"
                    [(ngModel)]="repeatIntervalStr" [ngModelOptions]="{standalone: true}" maxlength="1"
                    title="1–9">
                </div>
                <div class="su-rcl">
                  <span class="su-rcp">FREQUENCY</span>
                  <select class="su-rin" [(ngModel)]="repeatFrequency" [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onRepeatFrequencyChange($event)">
                    <option [ngValue]="'Daily'">Day</option>
                    <option [ngValue]="'Weekly'">Week</option>
                  </select>
                </div>
              </div>

              <ng-container *ngIf="repeatFrequency === 'Weekly'">
                <span class="su-rcp su-rcp--b">REPEAT ON</span>
                <div class="su-rdy">
                  <button type="button" *ngFor="let d of repeatDayLabels; let i = index"
                    class="su-dc" [class.su-dc--on]="repeatByDay[i]"
                    (click)="toggleRepeatDay(i)">{{ d }}</button>
                </div>
              </ng-container>

              <div class="su-er">
                <div class="su-rcl su-rcl--e">
                  <span class="su-rcp">ENDS</span>
                  <select class="su-rin" [(ngModel)]="recurrenceEndType" [ngModelOptions]="{standalone: true}">
                    <option [ngValue]="'never'">Never</option>
                    <option [ngValue]="'after'">After # occurrences</option>
                    <option [ngValue]="'on'">On date</option>
                  </select>
                </div>
                <div class="su-rcl" *ngIf="recurrenceEndType === 'after'">
                  <span class="su-rcp">OCCURRENCES</span>
                  <input type="text" inputmode="numeric" class="su-rin"
                    [(ngModel)]="recurrenceOccurrencesStr" [ngModelOptions]="{standalone: true}" maxlength="3"
                    title="1–999">
                </div>
                <div class="su-rcl" *ngIf="recurrenceEndType === 'on'">
                  <span class="su-rcp">END DATE</span>
                  <ng-container *ngIf="!useNativeSessionPickers">
                    <div class="su-dpr su-rin su-rdw" #recurrenceEndPickerAnchor>
                      <input
                        type="text"
                        class="su-rin su-id"
                        [(ngModel)]="recurrenceEndDate"
                        [ngModelOptions]="{standalone: true}"
                        ngbDatepicker
                        #recurrenceEndDp="ngbDatepicker"
                        [minDate]="ngbRecurrenceEndDateMinStruct()"
                        [maxDate]="ngbDateMaxBound()"
                        container="body"
                        placement="bottom-left"
                        autoClose="outside"
                        [outsideDays]="'visible'"
                        datepickerClass="su-ngdp"
                        placeholder="Select end date"
                        readonly
                        (click)="toggleBodyDatepicker(recurrenceEndDp, recurrenceEndPickerAnchor)"
                        (dateSelect)="recurrenceEndDp.close()"
                      />
                      <button type="button" class="su-dpb su-rcb" (click)="toggleBodyDatepicker(recurrenceEndDp, recurrenceEndPickerAnchor); $event.stopPropagation()" aria-label="Open calendar">
                        <i class="ri-calendar-line"></i>
                      </button>
                    </div>
                  </ng-container>
                  <input
                    *ngIf="useNativeSessionPickers"
                    type="date"
                    class="su-rin su-ntd"
                    [min]="recurrenceEndDateIsoMin()"
                    [max]="sessionDateIsoMax()"
                    [ngModel]="recurrenceEndDateIsoForNativeInput()"
                    [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onNativeRecurrenceEndDateIsoChange($event)"
                    (focus)="onNativeSessionFieldFocus($event)"
                  />
                </div>
              </div>

              <p class="su-rsu" *ngIf="getRecurrenceSummaryText()">{{ getRecurrenceSummaryText() }}</p>
            </div>
          </div>
        </form>
      </div>

      <div class="su-ft">
        <ng-container *ngIf="previewLoading || confirmLoading">
          <div class="su-fsp">
            <div class="spinner-border spinner-border-sm text-dark" role="status"><span class="sr-only">Loading…</span></div>
          </div>
        </ng-container>
        <ng-container *ngIf="!previewLoading && !confirmLoading">
          <button *ngIf="isEditing() && futureSessions() <= 1" type="button" class="su-b su-b2" (click)="onCancelSession(modal)">Cancel Session</button>
          <button *ngIf="isEditing() && futureSessions() > 1" type="button" class="su-b su-b2" (click)="onCancelAllSessions(modal)">{{ isGroupPtEditSlot() ? 'Cancel group session' : 'Cancel All Sessions' }}</button>
          <button *ngIf="!isEditing()" type="button" class="su-b su-b1 su-bp" [disabled]="!isFormValid()" (click)="onPreviewClick(modal)">Preview</button>
          <button *ngIf="isEditing()" type="button" class="su-b su-b1" [disabled]="!isFormValid()" (click)="onSubmit(modal)">Save</button>
        </ng-container>
      </div>
    </ng-container>
    </div>
  </div>
</ng-template>

<!-- Select members (select_trainer_member.dart) -->
<ng-template #selectMembersContent let-modal>
  <div class="sum-m">
    <div class="sum-h">
      <h2 class="sum-tt">Select members</h2>
      <p class="sum-st">{{ getSelectMembersSubtitle() }}</p>
      <button type="button" class="sum-x" aria-label="Close" (click)="closeSelectMembersModal()">
        <i class="ri-close-line"></i>
      </button>
    </div>

    <div class="sum-sw">
      <div class="sum-sf">
        <i class="ri-search-line sum-sic" aria-hidden="true"></i>
        <input type="text" id="select-members-search-input" class="sum-se" placeholder="Search by name"
          [ngModel]="memberSearchTerm" (ngModelChange)="onMemberSearchInput($event)" />
      </div>
    </div>

    <!-- Fixed-height strip: avoids layout jump when loading (no large block above the list). -->
    <div class="sum-slp" aria-hidden="true">
      <div *ngIf="clientsLoading" class="sum-slw" role="status">
        <span class="sr-only">Loading members</span>
        <span class="sum-sln"></span>
      </div>
    </div>

    <div class="sum-l">
      <ng-container *ngIf="!clientsLoading && membersForSelectModal.length === 0">
        <div class="sum-em">No members found</div>
      </ng-container>
      <button *ngFor="let member of membersForSelectModal; trackBy: memberTrackBy" type="button" class="sum-it"
        [class.sum-it--i]="member.isInactive"
        [class.sum-it--s]="isMemberSelected(member)"
        [disabled]="!addAllowed(member) || member.disabled"
        (click)="toggleMemberSelection(member)">
        <img [src]="member.avatar || 'assets/images/users/user-dummy-img.jpg'" alt="" class="sum-av" (error)="member.avatar='assets/images/users/user-dummy-img.jpg'" />
        <div class="sum-txt">
          <span class="sum-nm">{{ member.name }}</span>
          <span class="sum-me" *ngFor="let line of member.balanceLines">{{ line }}</span>
        </div>
        <i *ngIf="isMemberSelected(member)" class="ri-checkbox-circle-fill sum-ok" aria-hidden="true"></i>
      </button>
    </div>

    <div class="sum-ft">
      <button type="button" class="sum-dn" (click)="closeSelectMembersModal()">Done</button>
    </div>
  </div>
</ng-template>

<!-- Review booking (pt_session_booking_review_dialog.dart) -->
<ng-template #reviewBookingContent let-rmodal>
  <div class="sur-m">
    <div class="sur-hd">
      <div class="sur-hic" [class.sur-icw]="reviewHasIssues()" [class.sur-ick]="reviewPreview?.isValid && !reviewHasIssues()">
        <i class="ri-error-warning-fill" *ngIf="reviewHasIssues()"></i>
        <i class="ri-checkbox-circle-fill" *ngIf="!reviewHasIssues()"></i>
      </div>
      <div class="sur-hdt">
        <h2 class="sur-tt">Review booking</h2>
        <p class="sur-st">{{ getSessionTypeSummary() }}</p>
      </div>
      <button type="button" class="sur-x" aria-label="Close" (click)="rmodal.dismiss()">
        <i class="ri-close-line"></i>
      </button>
    </div>

    <div class="sur-bgs">
      <span class="sur-bd"><i class="ri-calendar-line"></i> {{ reviewSessionBadgeLabel }}</span>
      <span class="sur-bd"><i class="ri-group-line"></i> {{ reviewMemberBadgeLabel }}</span>
    </div>

    <div class="sur-is" *ngIf="reviewHasIssues()">
      <p class="sur-ill" *ngIf="isReviewConfirmDisabled()">
        <i class="ri-information-fill"></i> Confirm is disabled. Use Back to adjust the schedule, or review the details below.
      </p>
      <div class="sur-er" *ngIf="allReviewErrorsList().length">
        <div class="sur-sl warn">Preview messages</div>
        <ul>
          <li *ngFor="let e of allReviewErrorsList()">{{ e }}</li>
        </ul>
      </div>
      <ng-container *ngFor="let m of reviewPreview?.attendees || []">
        <div class="sur-msf" *ngIf="!m.hasSufficientBalance">
          <div class="sur-sl warn">Insufficient credits</div>
          <p class="sur-ssb">Required total minutes vs available balance:</p>
          <div class="sur-scd">
            <div class="sur-snm">{{ memberNameForPreview(m.memberId) }}</div>
            <div class="sur-xrw"><span>Required (total)</span><span>{{ m.requiredTotalMinutes }} min · {{ reviewPreview?.occurrenceCount }} occurrence(s)</span></div>
            <div class="sur-xrw"><span>Available balance</span><span>{{ m.balanceMinutes }} min</span></div>
            <div class="sur-xrw strong"><span>Short by</span><span class="sur-sgp">{{ m.requiredTotalMinutes - m.balanceMinutes }} min</span></div>
          </div>
        </div>
      </ng-container>
    </div>

    <div class="sur-sl">Schedule</div>
    <div class="sur-pn sur-pns">
      <div class="sur-szr" *ngFor="let occ of (reviewPreview?.occurrences || []); let i = index">
        <div class="sur-szb">
          <div class="sur-sdl">
            <span class="sur-six">{{ i + 1 }}.</span>
            <span class="sur-sdt">{{ formatOccurrenceDate(occ.startDate) }}</span>
          </div>
          <div class="sur-stm">{{ formatOccurrenceTimeRange(occ.startTime, occ.endTime) }}</div>
        </div>
      </div>
      <div class="sur-em" *ngIf="!(reviewPreview?.occurrences?.length)">No occurrences in preview.</div>
    </div>

    <div class="sur-sl">Members &amp; credits</div>
    <div class="sur-mls">
      <div class="sur-mc" *ngFor="let m of reviewPreview?.attendees || []">
        <div class="sur-mctx">
          <div class="sur-mnm">{{ memberNameForPreview(m.memberId) }}</div>
          <div class="sur-mme">{{ m.balanceMinutes }} min available · {{ m.requiredTotalMinutes }} min required</div>
        </div>
        <i class="ri-checkbox-circle-fill sur-mok" *ngIf="m.hasSufficientBalance" aria-hidden="true"></i>
        <i class="ri-close-circle-fill sur-mbd" *ngIf="!m.hasSufficientBalance" aria-hidden="true"></i>
      </div>
    </div>

    <div class="sur-ft">
      <button type="button" class="su-b su-b2" (click)="onReviewBack()">Back</button>
      <button type="button" class="su-b su-b1" [disabled]="isReviewConfirmDisabled() || confirmLoading" (click)="onReviewConfirm()">
        Confirm booking
      </button>
    </div>
  </div>
</ng-template>
